import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProductFormComponent } from '../../../../../shared/product-form/product-form.component';
import { Product } from '../../../../../shared/models/product-list';
import { ProductApiService } from '../../../services/product-api.service';
import { ToastNotificationService } from '../../../../../notifications/services/toast-notification.service';
import { ToastNotificationType, ToastPosition } from '../../../../../notifications/models/toast-notification-type';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ProductDetails } from '../../../../../shared/models/product-details';
import { NgIf, NgForOf } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../../shared/services/translations/language.service';
import { ApiErrorService } from '../../../../../shared/services/api-error.service';

@Component({
  selector: 'app-edit-product',
  imports: [FormsModule, ProductFormComponent, RouterLink, NgIf, NgForOf, TranslateModule],
  templateUrl: './edit-product.component.html',
  styleUrl: './edit-product.component.css'
})
export class EditProductComponent implements OnInit {

  apiService = inject(ProductApiService);
  apiErrorService = inject(ApiErrorService);
  productsResponse: ProductDetails | null = null;
  toastNotificationService = inject(ToastNotificationService);
  route = inject(ActivatedRoute);
  productId = Number(this.route.snapshot.paramMap.get('id'));
  router = inject(Router);
  disableSubmitButton = false;
  loading = false;
  translate = inject(TranslateService);
  languageService = inject(LanguageService);

  ngOnInit(): void {
    if (this.productId) {
      this.apiService.getProductById(this.productId).subscribe({
        next: (response) => {
          this.productsResponse = response;
        },
        error: (err) => {
          this.toastNotificationService.createNotification(this.translate.instant('products.errors.load_failed_message'), ToastNotificationType.ERROR);
        }
      });
    } else {
      this.toastNotificationService.createNotification(
        this.translate.instant('products.errors.generic_failed'),
        ToastNotificationType.ERROR
      );
    }
  }

  submit(form: Product) {
    this.disableSubmitButton = true;
    this.loading = true;
    if (!this.productsResponse) return;



    // --- Handle cover image ---
    // If the user did not change the cover image, keep the existing ID/URL
    if (form.cover_image === this.productsResponse.cover_image) {
      form.cover_image = undefined;
    }

    // --- Merge IDs for existing variant images ---
    form.variants = form.variants.map((variant, index) => {
      const existingVariant = this.productsResponse?.variants.find(v => v.id === variant.id);
      return {
        ...variant,
        id: existingVariant?.id,
        images: variant.images.map((img, i) => {


          const existingImage = existingVariant?.images?.find(img2 => img2.image === img.image);

          if (existingImage) {
            // Existing uploaded image → only send ID
            return { id: existingImage.id };
          }

          return {
            image: typeof img === 'string' ? img : img.image,
            alt_text: img.alt_text ?? undefined
          };

        }),
      };
    });

    this.apiService.updateProduct(this.productId, form).subscribe({
      next: (response) => {
        this.router.navigate(['/products/', this.productId]);
        this.toastNotificationService.createNotification(this.translate.instant('products.success.edited_message'), ToastNotificationType.SUCCESS, this.translate.instant('products.success.edited_title'), ToastPosition.TOP_RIGHT);
        this.disableSubmitButton = false;
        this.loading = false;
      },
      error: (err) => {
        this.toastNotificationService.createNotification(this.apiErrorService.getDisplayMessage(err, this.translate.instant('products.errors.generic_failed')), ToastNotificationType.ERROR, this.translate.instant('products.errors.edit_failed_title'), ToastPosition.TOP_RIGHT);
        this.disableSubmitButton = false;
        this.loading = false;
      },
    });
  }

}
